import { useState } from "react";
import api, { CATEGORIES, US_STATES } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Send } from "lucide-react";

export default function Submit() {
  const [form, setForm] = useState({
    title: "", description: "", suggested_url: "",
    submitter_name: "", submitter_email: "",
    category: "", jurisdiction: "",
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.description) { toast.error("Title and description are required."); return; }
    setLoading(true);
    try {
      await api.post("/tips", form);
      setSuccess(true);
      toast.success("Tip submitted. Thank you for the receipt.");
      setForm({ title: "", description: "", suggested_url: "", submitter_name: "", submitter_email: "", category: "", jurisdiction: "" });
    } catch { toast.error("Could not submit tip."); }
    finally { setLoading(false); }
  };

  return (
    <div className="max-w-[1100px] mx-auto px-6 py-12 grid lg:grid-cols-12 gap-10">
      <div className="lg:col-span-5">
        <div className="overline">/contribute</div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter mt-2">
          Have a receipt?
        </h1>
        <p className="mt-4 text-[#222] leading-relaxed">
          Submit a documented government action you believe belongs on the record. The strongest tips include a primary
          source link — court filing, bill text, agency rule, or established reporting.
        </p>
        <div className="mt-6 border-t border-black/15 pt-5 text-sm space-y-3 text-[#444]">
          <p><b className="text-black">What we accept:</b> Bills, court rulings, executive orders, agency rules, and published reporting.</p>
          <p><b className="text-black">What we don't:</b> Anonymous claims, opinion pieces without source links, or rumors.</p>
          <p><b className="text-black">Privacy:</b> Your email is optional and never published.</p>
        </div>
      </div>

      <form onSubmit={submit} className="lg:col-span-7 bg-white border border-black/15 p-6 space-y-5" data-testid="tip-form">
        {success ? (
          <div className="text-center py-12">
            <div className="font-display text-3xl font-bold">Tip received.</div>
            <p className="text-[#555] mt-2">An editor will review it. Thank you.</p>
            <Button onClick={() => setSuccess(false)} className="rounded-none bg-black text-white mt-6" data-testid="submit-another-btn">
              Submit another
            </Button>
          </div>
        ) : (
          <>
            <Field label="Title *">
              <Input data-testid="tip-title" value={form.title} onChange={(e) => update("title", e.target.value)} required className="rounded-none border-black/30 h-11" placeholder="e.g. New Texas bill restricts emergency contraception" />
            </Field>
            <Field label="Description *">
              <Textarea data-testid="tip-description" value={form.description} onChange={(e) => update("description", e.target.value)} required rows={5} className="rounded-none border-black/30" placeholder="What happened? Who did what, when, and where? Include any quotes." />
            </Field>
            <Field label="Source URL (strongly recommended)">
              <Input data-testid="tip-url" type="url" value={form.suggested_url} onChange={(e) => update("suggested_url", e.target.value)} className="rounded-none border-black/30 h-11" placeholder="https://…" />
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Category">
                <Select value={form.category} onValueChange={(v) => update("category", v)}>
                  <SelectTrigger data-testid="tip-category" className="rounded-none border-black/30 h-11"><SelectValue placeholder="Choose category" /></SelectTrigger>
                  <SelectContent className="rounded-none">
                    {CATEGORIES.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Jurisdiction">
                <Select value={form.jurisdiction} onValueChange={(v) => update("jurisdiction", v)}>
                  <SelectTrigger data-testid="tip-jurisdiction" className="rounded-none border-black/30 h-11"><SelectValue placeholder="Federal or state" /></SelectTrigger>
                  <SelectContent className="rounded-none max-h-72">
                    {US_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-4 pt-4 border-t border-black/10">
              <Field label="Your name (optional)">
                <Input data-testid="tip-name" value={form.submitter_name} onChange={(e) => update("submitter_name", e.target.value)} className="rounded-none border-black/30 h-11" />
              </Field>
              <Field label="Your email (optional)">
                <Input data-testid="tip-email" type="email" value={form.submitter_email} onChange={(e) => update("submitter_email", e.target.value)} className="rounded-none border-black/30 h-11" />
              </Field>
            </div>
            <Button type="submit" disabled={loading} data-testid="tip-submit-btn" className="rounded-none bg-black text-white hover:bg-[#333] h-12 w-full">
              <Send className="w-4 h-4 mr-2" /> {loading ? "Submitting…" : "Submit tip for review"}
            </Button>
          </>
        )}
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="overline mb-1.5 block">{label}</span>
      {children}
    </label>
  );
}
